import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Ban, Coins, Loader2, Rocket, Store } from "lucide-react";

import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { OnboardingChoiceCard } from "@/components/onboarding/OnboardingChoiceCard";
import type { FiscalBenefitDeclarationValue } from "@/components/features/fiscal-benefit/FiscalBenefitDeclaration";
import { isFiscalBenefitDeclarationComplete } from "@/components/features/fiscal-benefit/FiscalBenefitDeclaration";
import type { FiscalBenefitMotivo } from "@/services/fiscalService";

export interface RegimeStepProps {
  value: FiscalBenefitDeclarationValue;
  onChange: (next: FiscalBenefitDeclarationValue) => void;
  onContinue: () => void | Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
}

/**
 * Régimen step (self-onboarding): asks the Decreto 206/019 declaration BEFORE
 * plan selection, since the answer decides which plans are eligible and what
 * they cost. Presented as four plain-language choice cards — the owner is
 * assumed to know nothing about IVA mínimo / 80 UI — flattening the underlying
 * (complies, motivo) model so there is no confusing two-level radio.
 */

type RegimeChoice = "iva_minimo" | "ingresos_bajo_750k" | "inicio_actividades" | "none";

function choiceOf(value: FiscalBenefitDeclarationValue): RegimeChoice | null {
  if (value.complies === false) return "none";
  if (value.complies === true && value.motivo) return value.motivo;
  return null;
}

export function RegimeStep({
  value,
  onChange,
  onContinue,
  onBack,
  isSaving = false,
}: RegimeStepProps): JSX.Element {
  const { t } = useTranslation();
  const selected = choiceOf(value);

  const pick = (choice: RegimeChoice): void => {
    if (choice === "none") {
      onChange({ complies: false, motivo: null });
    } else {
      onChange({ complies: true, motivo: choice as FiscalBenefitMotivo });
    }
  };

  const options: {
    choice: RegimeChoice;
    icon: typeof Store;
    nameLead: string;
    nameAccent: string;
    desc: string;
    meta: string;
  }[] = [
    {
      choice: "iva_minimo",
      icon: Store,
      nameLead: t("onboarding.regimeStep.ivaMinimoLead", "Soy"),
      nameAccent: t("onboarding.regimeStep.ivaMinimoAccent", "pequeña empresa."),
      desc: t(
        "onboarding.regimeStep.ivaMinimoDesc",
        "Tributás por Literal E o IVA mínimo: facturás poco y estás exonerado de IVA/IRAE. En Literal E el tope es 305.000 UI de facturación al año.",
      ),
      meta: t(
        "onboarding.regimeStep.ivaMinimoMeta",
        "Te descontamos el crédito de 80 UI directo del precio: pagás menos",
      ),
    },
    {
      choice: "ingresos_bajo_750k",
      icon: Coins,
      nameLead: t("onboarding.regimeStep.ingresosLead", "Régimen general,"),
      nameAccent: t("onboarding.regimeStep.ingresosAccent", "facturo poco."),
      desc: t(
        "onboarding.regimeStep.ingresosDesc",
        "Estás en régimen general pero el año pasado facturaste menos de 750.000 UI. Cumplís los requisitos del beneficio de DGI.",
      ),
      meta: t(
        "onboarding.regimeStep.ingresosMeta",
        "Accedés al crédito de 80 UI; lo recuperás en tu declaración de DGI",
      ),
    },
    {
      choice: "inicio_actividades",
      icon: Rocket,
      nameLead: t("onboarding.regimeStep.inicioLead", "Recién"),
      nameAccent: t("onboarding.regimeStep.inicioAccent", "arranqué."),
      desc: t(
        "onboarding.regimeStep.inicioDesc",
        "Estás en régimen general y empezaste a facturar este año (inicio de actividades). También cumplís los requisitos del beneficio.",
      ),
      meta: t(
        "onboarding.regimeStep.inicioMeta",
        "Accedés al crédito de 80 UI; lo recuperás en tu declaración de DGI",
      ),
    },
    {
      choice: "none",
      icon: Ban,
      nameLead: t("onboarding.regimeStep.noneLead", "No me"),
      nameAccent: t("onboarding.regimeStep.noneAccent", "corresponde."),
      desc: t(
        "onboarding.regimeStep.noneDesc",
        "No cumplís los requisitos, o preferís no declararlo ahora. Si sos monotributo no tenés obligación de facturar electrónico y el beneficio no aplica: elegí esta opción. Ante la duda, elegí esta también — se puede cambiar después desde Configuración.",
      ),
      meta: t(
        "onboarding.regimeStep.noneMeta",
        "Pagás el precio completo de tu plan",
      ),
    },
  ];

  const ctaDisabled = !isFiscalBenefitDeclarationComplete(value) || isSaving;

  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={1}
            total={2}
            label={t("onboarding.regimeStep.tag", "Régimen")}
            flow={t("onboarding.planPhase.flow", "Tu plan")}
          />
          <h1 className="fx-onb-h1">
            {t("onboarding.regimeStep.h1Lead", "¿Cómo")}{" "}
            <span className="ital">
              {t("onboarding.regimeStep.h1Accent", "tributás?")}
            </span>
          </h1>
          <p className="fx-onb-lead">
            {t(
              "onboarding.regimeStep.lead",
              "DGI le da un crédito de 80 UI por mes ($514*) a quienes emiten factura electrónica. Según tu caso lo descontamos del precio o lo recuperás vos — decinos cuál es el tuyo.",
            )}
          </p>

          <div className="fx-onb-choice-grid stack">
            {options.map(({ choice, icon: Icon, nameLead, nameAccent, desc, meta }) => (
              <OnboardingChoiceCard
                key={choice}
                icon={<Icon aria-hidden />}
                name={
                  <>
                    {nameLead} <span className="ital">{nameAccent}</span>
                  </>
                }
                desc={desc}
                meta={<span className="meta-outcome">{meta}</span>}
                selected={selected === choice}
                onClick={() => pick(choice)}
              />
            ))}
          </div>

          <div className="fx-onb-actions">
            <div className="side">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-secondary"
                onClick={onBack}
                disabled={isSaving}
              >
                <ArrowLeft aria-hidden />
                {t("onboarding.back", "Volver")}
              </button>
            </div>
            <div className="side end">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                disabled={ctaDisabled}
                onClick={() => {
                  if (ctaDisabled) return;
                  void onContinue();
                }}
              >
                {isSaving ? (
                  <Loader2 className="animate-spin" aria-hidden />
                ) : (
                  <>
                    {t("onboarding.next", "Siguiente")}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={t(
            "onboarding.regimeStep.brandEyebrow",
            "Un crédito que DGI ya te da",
          )}
          quote={
            <>
              {t("onboarding.regimeStep.brandQuoteLead", "80 UI por mes")}{" "}
              <span className="accent">
                {t("onboarding.regimeStep.brandQuoteAccent", "de vuelta")}
              </span>{" "}
              {t(
                "onboarding.regimeStep.brandQuoteTail",
                "por emitir factura electrónica. Te ayudamos a tomarlo bien.",
              )}
            </>
          }
          valueBlock={{
            head: t("onboarding.regimeStep.valueHead", "Cómo se aplica"),
            items: [
              t(
                "onboarding.regimeStep.value1",
                "Pequeña empresa / IVA mínimo: lo descontamos del precio, pagás menos.",
              ),
              t(
                "onboarding.regimeStep.value2",
                "Régimen general: lo tomás en tu declaración; el precio no cambia.",
              ),
              t(
                "onboarding.regimeStep.value3",
                "¿Dudas? Elegí lo que mejor te describa; se puede cambiar después.",
              ),
            ],
          }}
          afterContent={
            <p style={{ fontSize: 12, opacity: 0.55, marginTop: 20 }}>
              {t(
                "onboarding.regimeStep.uiDisclaimer",
                "* El equivalente en pesos ($514) surge del valor vigente de la UI y se ajusta con él.",
              )}
            </p>
          }
        />
      }
    />
  );
}
